import { Node } from "reactflow";
import {
  logicNodes,
  dataTransmission,
  customNodes,
  lsNodes,
  codeNodes,
} from "../common/nodeTemplates";

const buildNodeStylesMap = () => {
  const stylesMap = new Map<
    string,
    { borderColor: string; shadowColor: string; iconStyle: string }
  >();

  // Map all node templates to their styles
  const allNodes = [
    ...logicNodes,
    ...dataTransmission,
    ...customNodes,
    ...lsNodes,
    ...codeNodes,
  ];

  allNodes.forEach((node) => {
    stylesMap.set(node.type, {
      borderColor: node.borderColor,
      shadowColor: node.shadowColor,
      iconStyle: node.iconStyle || "text-muted-foreground",
    });
  });

  // Add backend type aliases that map to the same styles
  const typeAliases: Record<string, string> = {
    // Start aliases
    start: "startNode",
    manual_trigger: "startNode",

    // HTTP Request aliases
    http_request: "requestBlock",

    // Edit Fields aliases
    edit_fields: "editFields",
    set_node: "editFields",

    // If/Else aliases
    if_else: "ifElseNode",

    // Switch aliases
    switch: "switchNode",
    switch_case: "switchNode",

    // Split Out aliases
    split_out: "splitOutNode",

    // Aggregate aliases
    aggregate: "aggregateNode",

    // Merge aliases
    merge_items: "mergeNode",
    merge: "mergeNode", // Legacy support

    // Sort aliases
    sort_node: "sortNode",
    sort: "sortNode",

    // Limit aliases
    limit_node: "limitNode",
    limit: "limitNode",

    // Save Logs aliases
    save_to_logs: "savelogs",
    save__to_parquet: "savelogs",

    // Save Datalist aliases
    save_to_datalist: "savedatalist",

    // Python Code aliases
    python_code: "pythonCodeNode",

    // Function Node aliases
    functional_node_linkshadow: "functionNode",

    // Stop and Error aliases
    stop_and_error: "stopAndErrorNode",

    // Connect Flow aliases
    sub_workflow_node: "connectFlow",
  };

  // Map all aliases to their corresponding styles
  Object.entries(typeAliases).forEach(([alias, baseType]) => {
    const baseStyle = stylesMap.get(baseType);
    if (baseStyle) {
      stylesMap.set(alias, baseStyle);
    }
  });

  return stylesMap;
};

// Create the styles map once at module load time
const nodeStylesMap = buildNodeStylesMap();

/**
 * Get default label for a node based on its sub_type
 */
export const getDefaultNodeLabel = (subType: string): string => {
  if (subType === "manual_trigger" || subType === "start") return "Start";
  if (subType === "http_request") return "HTTP Request";
  if (subType === "if_else") return "If Else";
  if (subType === "switch" || subType === "switch_case") return "Switch";
  if (subType === "edit_fields" || subType === "set_node") return "Edit Fields";
  if (subType === "split_out") return "Split Out";
  if (subType === "aggregate") return "Aggregate";
  if (subType === "merge_items" || subType === "merge") return "Merge";
  if (subType === "loop_over_items") return "Loop Over Items";
  if (subType === "sort_node" || subType === "sort") return "Sort";
  if (subType === "limit_node" || subType === "limit") return "Limit";
  if (subType === "save_to_logs") return "Save Logs";
  if (subType === "save__to_parquet") return "Save Logs";
  if (subType === "save_to_datalist") return "Save Datalist";
  if (subType === "authentication" || subType === "config_node")
    return "Authentication";
  if (subType === "python_code") return "Python Code";
  if (subType === "functional_node_linkshadow") return "Function";
  if (subType === "stop_and_error") return "Stop and Error";
  if (subType === "sub_workflow_node") return "Connect Flow";
  return "Node"; // Fallback
};

/**
 * Get default styles for a node based on its sub_type
 * Uses the centralized nodeTemplates.ts for all style definitions
 */
export const getDefaultNodeStyles = (subType: string) => {
  // Look up styles from the map built from nodeTemplates
  const styles = nodeStylesMap.get(subType);

  if (styles) {
    return styles;
  }

  // Special cases not in nodeTemplates
  if (
    subType === "startNode" ||
    subType === "start" ||
    subType === "manual_trigger"
  ) {
    return {
      borderColor: "var(--node-start)",
      shadowColor: "var(--node-shadow-start)",
      iconStyle: "text-muted-foreground",
    };
  } else if (
    subType === "authentication" ||
    subType === "configurationNode" ||
    subType === "config_node"
  ) {
    return {
      borderColor: "var(--primary)",
      shadowColor: "var(--light-red)",
      iconStyle: "text-muted-foreground",
    };
  } else if (subType === "loop_over_items" || subType === "loopOverItemsNode") {
    return {
      borderColor: "var(--node-loop-over)",
      shadowColor: "var(--node-shadow-loop-over)",
      iconStyle: "text-muted-foreground",
    };
  }
  // Fallback for unknown node types
  return {
    borderColor: "var(--primary)",
    shadowColor: "var(--node-shadow-default)",
    iconStyle: "text-muted-foreground",
  };
};

/**
 * Map backend sub_type to frontend node type
 */
export const mapBackendTypeToFrontend = (subType: string): string => {
  if (subType === "start" || subType === "manual_trigger") return "startNode";
  if (subType === "http_request") return "requestBlock";
  if (subType === "save_to_logs" || subType === "save__to_parquet")
    return "savelogs";
  if (subType === "save_to_datalist") return "savedatalist";
  if (subType === "edit_fields" || subType === "set_node") return "editFields";
  if (subType === "if_else") return "ifElseNode";
  if (subType === "switch" || subType === "switch_case") return "switchNode";
  if (subType === "split_out") return "splitOutNode";
  if (subType === "aggregate") return "aggregateNode";
  if (subType === "merge_items" || subType === "merge") return "mergeNode"; // Support both new and legacy merge nodes
  if (subType === "loop_over_items") return "loopOverItemsNode";
  if (subType === "config_node" || subType === "authentication")
    return "authentication"; // Config nodes
  if (subType === "sort_node" || subType === "sort") return "sortNode";
  if (subType === "limit_node" || subType === "limit") return "limitNode";
  if (subType === "python_code") return "pythonCodeNode";
  if (subType === "functional_node_linkshadow") return "functionNode";
  if (subType === "stop_and_error") return "stopAndErrorNode";
  if (subType === "sub_workflow_node") return "connectFlow";
  return subType;
};

// centralizes the mapping of frontend node types to backend types and subtypes.
export const getNodeBackendType = (
  frontendNodeType: string,
): { type: string; sub_type: string } => {
  // Default type
  let backendType = "core";
  let backendSubType = frontendNodeType || "custom";

  // Map frontend node types to backend types and subtypes
  if (frontendNodeType === "requestBlock") {
    backendType = "core";
    backendSubType = "http_request";
  } else if (frontendNodeType === "savelogs") {
    backendType = "save";
    backendSubType = "save_to_logs";
  } else if (frontendNodeType === "savedatalist") {
    backendType = "save";
    backendSubType = "save_to_datalist";
  } else if (
    frontendNodeType === "editFields" ||
    frontendNodeType === "editFieldsNode"
  ) {
    backendType = "data_transformation";
    backendSubType = "set_node";
  } else if (frontendNodeType === "splitOutNode") {
    backendType = "data_transformation";
    backendSubType = "split_out";
  } else if (frontendNodeType === "aggregateNode") {
    backendType = "data_transformation";
    backendSubType = "aggregate";
  } else if (frontendNodeType === "mergeNode") {
    backendType = "flow";
    backendSubType = "merge_items";
  } else if (frontendNodeType === "sortNode") {
    backendType = "data_filtration";
    backendSubType = "sort_node";
  } else if (frontendNodeType === "limitNode") {
    backendType = "data_filtration";
    backendSubType = "limit_node";
  } else if (
    frontendNodeType === "ifBlock" ||
    frontendNodeType === "ifElseNode"
  ) {
    backendType = "flow";
    backendSubType = "if_else";
  } else if (frontendNodeType === "switchNode") {
    backendType = "flow";
    backendSubType = "switch_case";
  } else if (frontendNodeType === "startNode") {
    backendType = "flow";
    backendSubType = "manual_trigger";
  } else if (frontendNodeType === "loopOverItemsNode") {
    backendType = "flow";
    backendSubType = "loop_over_items";
  } else if (frontendNodeType === "pythonCodeNode") {
    backendType = "code_node";
    backendSubType = "python_code";
  } else if (frontendNodeType === "functionNode") {
    backendType = "function_node";
    backendSubType = "functional_node_linkshadow";
  } else if (frontendNodeType === "stopAndErrorNode") {
    backendType = "error";
    backendSubType = "stop_and_error";
  } else if (
    frontendNodeType === "authentication" ||
    frontendNodeType === "configurationNode"
  ) {
    backendType = "config";
    backendSubType = "config_node";
  } else if (frontendNodeType === "exportedMeshflow") {
    backendType = "core";
    backendSubType = "exportedMeshflow";
  } else if (frontendNodeType === "connectFlow") {
    backendType = "core";
    backendSubType = "sub_workflow_node";
  }

  return { type: backendType, sub_type: backendSubType };
};

// This function transforms a backend node object to the format expected by React Flow, including handling various node types and their specific parameters, as well as applying default styles and labels based on the node's sub_type.
export const transformBackendNodeToReactFlow = (
  node: any,
  handlers: {
    onDelete: (nodeId: string) => void;
    onDataChange: (nodeId: string, updates: any) => void;
  },
): Node => {
  // MIGRATION: Convert old "merge" sub_type to "merge_items" for backward compatibility
  let subType = node.node_sub_type || node.sub_type;
  if (subType === "merge") {
    subType = "merge_items";
    // Also update the node object to use the new sub_type
    if (node.node_sub_type) node.node_sub_type = "merge_items";
    if (node.sub_type) node.sub_type = "merge_items";
  }

  const defaultStyles = getDefaultNodeStyles(subType);
  const params = node.parameters || {};

  const { ...cleanParams } = params;

  // Map backend sub_type to frontend node type
  const nodeType = mapBackendTypeToFrontend(subType);

  // Build data object based on node type
  let nodeData: any = {
    uid: node.node_uid || node.uid,
    title: node.node_title || node.title,
    description:
      node.node_description ||
      node.description ||
      cleanParams.description ||
      "",
    header: {
      label: node.label || getDefaultNodeLabel(subType),
    },
    label: getDefaultNodeLabel(subType), // Keep for backward compatibility
    borderColor: defaultStyles.borderColor, // Always use default style
    shadowColor: defaultStyles.shadowColor,
    iconStyle: defaultStyles.iconStyle,
    onDelete: handlers.onDelete,
    onDataChange: handlers.onDataChange,
  };

  // For requestBlock, store parameters as storedParameters for proper loading
  if (nodeType === "requestBlock") {
    nodeData.storedParameters = {
      ...cleanParams,
      description:
        node.node_description ||
        node.description ||
        cleanParams.description ||
        "",
    };
  } else if (nodeType === "editFields") {
    // For editFields, add specific fields
    nodeData.mode = cleanParams.mode || "manual";

    // Transform backend fields.values format to frontend fields array
    let fields = [];
    if (
      cleanParams.fields?.values &&
      Array.isArray(cleanParams.fields.values)
    ) {
      fields = cleanParams.fields.values.map((field: any, index: number) => {
        const valueType = field.type || "stringValue";
        const value = field[valueType] !== undefined ? field[valueType] : "";

        return {
          id: `field_${Date.now()}_${index}`,
          fieldName: field.name || "",
          fieldValue: value,
          fieldType: "fixed",
          fieldValueType: valueType,
          enabled: true,
        };
      });
    } else if (cleanParams.fields && Array.isArray(cleanParams.fields)) {
      fields = cleanParams.fields;
    }

    nodeData.fields = fields;
    nodeData.includeOtherInputFields =
      cleanParams.includeOtherFields ||
      cleanParams.includeOtherInputFields ||
      false;
    nodeData.globalFieldType = cleanParams.globalFieldType || "fixed";
    nodeData.inputFieldsMode =
      cleanParams.include || cleanParams.inputFieldsMode || "all";

    // Transform selectedFields/excludeFields back to inputFieldsList
    let inputFieldsList = "";
    if (cleanParams.selectedFields) {
      const fields = cleanParams.selectedFields.split(",");
      inputFieldsList = fields
        .map((f: string) => `{{$json.${f.trim()}}}`)
        .join(", ");
    } else if (cleanParams.excludeFields) {
      const fields = cleanParams.excludeFields.split(",");
      inputFieldsList = fields
        .map((f: string) => `{{$json.${f.trim()}}}`)
        .join(", ");
    } else {
      inputFieldsList = cleanParams.inputFieldsList || "";
    }
    nodeData.inputFieldsList = inputFieldsList;

    // Clean up storedParameters - remove selectedFields/excludeFields if not needed
    const editStoredParams = { ...cleanParams };
    if (editStoredParams.include !== "selected") {
      delete editStoredParams.selectedFields;
    }
    if (editStoredParams.include !== "except") {
      delete editStoredParams.excludeFields;
    }
    nodeData.storedParameters = editStoredParams;
  } else if (nodeType === "savelogs") {
    // For savelogs, add log-specific fields
    nodeData.logUid = cleanParams.logUid || "";
    nodeData.logTitle = cleanParams.logTitile || cleanParams.logTitle || "";
    nodeData.logType = cleanParams.logType || "custom";
    nodeData.cronFrequency = cleanParams.cronFrequency || null;
    nodeData.logCategory = cleanParams.logCategory || "";
    nodeData.log_ls_product_type = cleanParams.log_ls_product_type || [];

    // Transform mappingFields from backend format to logMappings format
    if (cleanParams.mappingFields && Array.isArray(cleanParams.mappingFields)) {
      nodeData.logMappings = cleanParams.mappingFields.map((field: any) => ({
        id: `mapping_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        fieldPath: field.rawFieldName ? `$json.${field.rawFieldName}` : "",
        fieldKey: field.rawFieldName || "",
        fieldValue: null,
        mappedLsField: field.mappingLsField || "",
        displayName: field.display_name || "",
        category: field.category || "",
        isTime: field.is_time || false,
        enabled: true,
      }));
    } else {
      nodeData.logMappings = cleanParams.logMappings || [];
    }

    nodeData.storedParameters = cleanParams;
  } else if (nodeType === "savedatalist") {
    // For savedatalist, add datalist-specific fields
    nodeData.datalistUid = cleanParams.datalistUid || "";
    nodeData.datalistTitle =
      cleanParams.dataTitile || cleanParams.datalistTitle || "";
    nodeData.cronType =
      cleanParams.cronTimeType || cleanParams.cronType || "run_once";
    nodeData.groupBy =
      cleanParams.typeDatalist || cleanParams.groupBy || "hash";

    // Transform mappingDataList or mappingFields from backend format
    const backendMappings =
      cleanParams.mappingDataList || cleanParams.mappingFields;
    if (backendMappings && Array.isArray(backendMappings)) {
      nodeData.datalistMappings = backendMappings.map((field: any) => ({
        id: `mapping_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        fieldPath: field.rawFieldName ? `$json.${field.rawFieldName}` : "",
        fieldKey: field.rawFieldName || "",
        fieldValue: null,
        mappedLsField: field.mappingLsField || "",
        displayName: field.display_name || "",
        columnType: field.column_type || "",
        dataType: field.data_type || "",
        enabled: true,
      }));
    } else {
      nodeData.datalistMappings = cleanParams.datalistMappings || [];
    }

    nodeData.storedParameters = cleanParams;
  } else if (nodeType === "ifBlock") {
    // For ifBlock, add conditions
    nodeData.conditions = cleanParams.conditions || [];
  } else if (nodeType === "ifElseNode") {
    // For ifElseNode, add conditions and options
    nodeData.conditions = cleanParams.conditions || {
      options: {
        caseSensitive: true,
        leftValue: "",
        typeValidation: "strict",
        version: 3,
      },
      conditions: [],
      combinator: "and",
    };
    nodeData.looseTypeValidation = cleanParams.looseTypeValidation || false;
    nodeData.options = cleanParams.options || {};

    nodeData.storedParameters = cleanParams;
    nodeData.node_data = cleanParams.node_data || [
      {
        id: "true",
        outputKey: "True",
        itemCount: 0,
      },
      {
        id: "false",
        outputKey: "False",
        itemCount: 0,
      },
    ];

    nodeData.storedParameters = cleanParams;
  } else if (nodeType === "switchNode") {
    // For switchNode, add mode and rules
    nodeData.mode = cleanParams.mode || "rules";
    nodeData.rules = cleanParams.rules || { values: [] };
    nodeData.looseTypeValidation = cleanParams.looseTypeValidation || true;
    nodeData.options = cleanParams.options || {};
    nodeData.node_data = cleanParams.node_data || []; // Track output handles

    nodeData.storedParameters = cleanParams;
  } else if (nodeType === "splitOutNode") {
    // For splitOutNode, store parameters for proper save/load cycle
    nodeData.fieldsToSplitOut = cleanParams.fieldToSplitOut
      ? [cleanParams.fieldToSplitOut]
      : [""];
    nodeData.include = cleanParams.include || "noOtherFields";
    nodeData.fieldsToInclude = cleanParams.fieldsToInclude
      ? [cleanParams.fieldsToInclude]
      : [""];
    nodeData.options = cleanParams.options || {
      disableDotNotation: false,
      destinationFieldName: "",
    };

    // Clean up storedParameters - AGGRESSIVE cleanup for fieldsToInclude
    const splitStoredParams = { ...cleanParams };
    // Set fieldsToInclude to empty string when not needed (backend expects field to exist)
    // Delete fieldsToInclude if:
    // 1. Include mode is not selectedOtherFields
    // 2. It's an array (should be string in backend)
    // 3. It's empty/falsy
    const shouldSetFieldsToEmpty =
      splitStoredParams.include !== "selectedOtherFields" ||
      Array.isArray(splitStoredParams.fieldsToInclude) ||
      !splitStoredParams.fieldsToInclude ||
      (typeof splitStoredParams.fieldsToInclude === "string" &&
        !splitStoredParams.fieldsToInclude.trim());

    if (shouldSetFieldsToEmpty) {
      splitStoredParams.fieldsToInclude = "";
    }
    nodeData.storedParameters = splitStoredParams;
  } else if (nodeType === "aggregateNode") {
    // For aggregateNode, store parameters for proper save/load cycle
    nodeData.aggregate = cleanParams.aggregate || "aggregateAllItemData";
    nodeData.fieldsToAggregate = cleanParams.fieldsToAggregate || null;
    nodeData.destinationFieldName = cleanParams.destinationFieldName || "";
    nodeData.include = cleanParams.include || "allFields";
    nodeData.fieldsToInclude = cleanParams.fieldsToInclude || "";
    nodeData.fieldsToExclude = cleanParams.fieldsToExclude || "";
    nodeData.options = cleanParams.options || {
      keepMissing: false,
      mergeLists: false,
      disableDotNotation: false,
    };

    // Clean up storedParameters - AGGRESSIVE cleanup for fieldsToInclude/fieldsToExclude
    const aggStoredParams = { ...cleanParams };
    // Set fieldsToInclude to empty string when not needed (backend expects field to exist)
    if (
      aggStoredParams.include !== "specifiedFields" ||
      !aggStoredParams.fieldsToInclude ||
      (typeof aggStoredParams.fieldsToInclude === "string" &&
        !aggStoredParams.fieldsToInclude.trim())
    ) {
      aggStoredParams.fieldsToInclude = "";
    }
    // Set fieldsToExclude to empty string when not needed (backend expects field to exist)
    if (
      aggStoredParams.include !== "allFieldsExcept" ||
      !aggStoredParams.fieldsToExclude ||
      (typeof aggStoredParams.fieldsToExclude === "string" &&
        !aggStoredParams.fieldsToExclude.trim())
    ) {
      aggStoredParams.fieldsToExclude = "";
    }
    nodeData.storedParameters = aggStoredParams;
  } else if (nodeType === "mergeNode") {
    // For mergeNode, store parameters for proper save/load cycle
    nodeData.mode = cleanParams.mode || "append";
    nodeData.numberOfInputs = cleanParams.numberOfInputs || 2;
    nodeData.combineBy = cleanParams.combineBy || "combineByPosition";
    nodeData.fieldNamesMatch =
      cleanParams.fieldNamesMatch !== undefined
        ? cleanParams.fieldNamesMatch
        : true;
    nodeData.fieldsToMatch = cleanParams.fieldsToMatch || "";
    nodeData.outputType = cleanParams.outputType || "keepMatches";
    nodeData.outputDataFrom = cleanParams.outputDataFrom || "both";

    // Handle options object
    nodeData.disableDotNotation = cleanParams.disableDotNotation;
    nodeData.fuzzyCompare = cleanParams.fuzzyCompare;

    nodeData.storedParameters = cleanParams;
  } else if (nodeType === "sortNode") {
    // For sortNode, store parameters for proper save/load cycle
    // Map backend mode to UI mode
    const backendMode = cleanParams.mode || "field";
    const uiMode =
      backendMode === "field"
        ? "simple"
        : backendMode === "custom"
          ? "code"
          : "random";

    nodeData.mode = uiMode;
    nodeData.nullsLast = cleanParams.nullsLast ?? true;
    nodeData.disableDotNotation =
      cleanParams.options?.disableDotNotation ?? true;

    // Transform fields from backend format
    if (backendMode === "field" && cleanParams.fields) {
      nodeData.fields = cleanParams.fields.map((field: any, index: number) => ({
        id: `field_${Date.now()}_${index}`,
        field: field.field || "",
        order: field.order || "asc",
      }));
    } else {
      nodeData.fields = [];
    }

    // For custom mode
    if (backendMode === "custom") {
      nodeData.customCode = cleanParams.customCode || "";
      nodeData.order = cleanParams.order || "asc";
    }

    // Clean up storedParameters based on mode (keep backend mode in storedParameters)
    const sortStoredParams = { ...cleanParams };
    if (sortStoredParams.mode === "random") {
      delete sortStoredParams.fields;
      delete sortStoredParams.customCode;
      delete sortStoredParams.order;
    } else if (sortStoredParams.mode === "field") {
      delete sortStoredParams.customCode;
      delete sortStoredParams.order;
    } else if (sortStoredParams.mode === "custom") {
      delete sortStoredParams.fields;
    }
    nodeData.storedParameters = sortStoredParams;
  } else if (nodeType === "limitNode") {
    // For limitNode, store parameters for proper save/load cycle
    nodeData.maxItems = cleanParams.maxItems || 10;
    nodeData.keepItemsFrom = cleanParams.keepItemsFrom || "start";
    nodeData.storedParameters = cleanParams;
  } else if (nodeType === "startNode") {
    // For startNode, always set response to [{}] - this is the initial data it provides
    nodeData.response = [{}];
    nodeData.outputData = [{}];
    nodeData = { ...nodeData, ...cleanParams };
  } else if (nodeType === "functionNode") {
    // For functionNode, handle threat lookup parameters
    nodeData.action = cleanParams.action || "threat_lookup";

    // Transform mappingDataList from backend format
    if (
      cleanParams.mappingDataList &&
      Array.isArray(cleanParams.mappingDataList)
    ) {
      nodeData.mappingDataList = cleanParams.mappingDataList.map(
        (m: any, index: number) => ({
          id: `mapping_${Date.now()}_${index}`,
          key: m.key || "",
          values: m.values || "",
          is_custom: m.is_custom !== undefined ? m.is_custom : false,
          data_type: m.data_type || "string",
          display_name: m.display_name || "",
          is_required: m.is_required || false,
          enabled: true,
        }),
      );
    } else {
      nodeData.mappingDataList = [];
    }

    nodeData.storedParameters = cleanParams;
  } else if (nodeType === "authentication") {
    // For authentication/config nodes, store parameters for UID lookup
    // Don't set configData here - let FlowBuilder.tsx look up fresh API data
    nodeData.parameters = {
      TabId: cleanParams.TabId || cleanParams.tabId,
      Uid: cleanParams.Uid || cleanParams.uid,
      uid: cleanParams.Uid || cleanParams.uid,
      tabId: cleanParams.TabId || cleanParams.tabId,
      configType: cleanParams.configType || "temp",
      pluginId: cleanParams.pluginId,
      title:
        cleanParams.configData?.title ||
        cleanParams.title ||
        node.node_title ||
        node.title,
    };
    // Don't set configData from saved workflow - use fresh API data in FlowBuilder
    nodeData.configData = null;
  } else if (nodeType === "connectFlow") {
    // For connectFlow, store parameters for proper save/load cycle
    nodeData.storedParameters = {
      workflow_id: cleanParams.workflow_id || "",
      mode: cleanParams.mode || "once",
    };
  } else {
    // For other nodes, spread all parameters
    nodeData = { ...nodeData, ...cleanParams };
  }

  return {
    id: node.node_uid || node.uid,
    type: nodeType,
    position: {
      x: (node.node_position || node.position)?.[0] || 0,
      y: (node.node_position || node.position)?.[1] || 0,
    },
    data: nodeData,
  };
};

// MIGRATION: This function also handles transforming old frontend nodes with "mergeNode" type to the new "merge_items" backend sub_type for backward compatibility
export const transformNodeToBackend = (node: Node, pluginId: string = "") => {
  // Determine the correct backend type based on node type using centralized mapping
  const { type: backendType, sub_type: backendSubType } = getNodeBackendType(
    node.type || "custom",
  );

  const baseNode = {
    uid: node.id,
    title: node.data.title || node.data.header || "Untitled Node",
    label: getDefaultNodeLabel(backendSubType),
    ...(node.data.description && { description: node.data.description }),
    type: backendType,
    sub_type: backendSubType,
    version: "1.0",
    position: [node.position.x, node.position.y],
    parameters: {} as any,
  };

  // Handle different node types
  if (node.type === "requestBlock") {
    // Use storedParameters if available, otherwise build from individual fields
    if (node.data.storedParameters) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { node_type: _node_type, ...cleanParams } =
        node.data.storedParameters;
      baseNode.parameters = {
        ...cleanParams,
        ...(node.data.description && { description: node.data.description }),
      };
    } else {
      baseNode.parameters = {
        url: node.data.url || "",
        method: node.data.method || "GET",
        params: node.data.params || {
          type: "none",
          parameter: [],
        },
        headers: node.data.headers || [],
        auth: node.data.auth || {
          type: "none",
          token: "",
          username: "",
          password: "",
        },
        flag: 1,
        plugin_id: pluginId,
        ssl_verify: node.data.ssl_verify !== false,
        config: node.data.config || {},
        data: node.data.data || {},
        body_type: node.data.body_type || "none",
        body: node.data.body || null,
        raw_type: node.data.raw_type || null,
        filteration: node.data.filteration || {
          type: "none",
          default_cron_start_time: "none",
          start_key: "",
          end_key: "",
          custom_date_format: "",
        },
        pagination: node.data.pagination || {
          type: "none",
        },
        ...(node.data.description && { description: node.data.description }),
      };
    }
  } else if (
    node.type === "authentication" ||
    node.type === "configurationNode"
  ) {
    // Configuration node - structure matches backend expectation
    const configData = node.data.configData || {};
    const savedParams = node.data.parameters || {};
    const isDeleted = node.data.isDeleted === true;

    // Extract TabId and Uid - prioritize configData, fallback to saved parameters
    const tabId =
      configData.tabId ||
      configData.TabId ||
      savedParams.TabId ||
      savedParams.tabId ||
      null;
    const uid =
      configData.uid ||
      configData.Uid ||
      savedParams.Uid ||
      savedParams.uid ||
      null;
    const configType =
      configData.configType ||
      configData.type ||
      savedParams.configType ||
      "temp";

    baseNode.parameters = {
      TabId: tabId,
      Uid: uid,
      pluginId: pluginId,
      configType: configType,
      configData: {
        ...configData,
      },
      isDeleted: isDeleted, // Mark as deleted for backend awareness
    };
  } else if (node.type === "ifBlock") {
    baseNode.parameters = {
      conditions: node.data.conditions || [],
    };
    // AFTER — always include node_data:
  } else if (node.type === "ifElseNode") {
    baseNode.type = "flow";
    baseNode.sub_type = "if_else";

    if (node.data.storedParameters) {
      const { ...cleanParams } = node.data.storedParameters;
      baseNode.parameters = {
        ...cleanParams,
        // Explicitly persist node_data from live node state (overrides stale storedParameters copy)
        node_data: node.data.node_data ||
          cleanParams.node_data || [
            { id: "true", outputKey: "True", itemCount: 0 },
            { id: "false", outputKey: "False", itemCount: 0 },
          ],
      };
    } else {
      baseNode.parameters = {
        conditions: {
          options: node.data.conditions?.options || {
            caseSensitive: true,
            leftValue: "",
            typeValidation: "strict",
            version: 3,
          },
          conditions: node.data.conditions?.conditions || [],
          combinator: node.data.conditions?.combinator || "and",
        },
        looseTypeValidation: node.data.looseTypeValidation || false,
        options: node.data.options || {},
        node_data: node.data.node_data || [
          { id: "true", outputKey: "True", itemCount: 0 },
          { id: "false", outputKey: "False", itemCount: 0 },
        ],
      };
    }
  } else if (node.type === "switchNode") {
    // Explicitly set type and sub_type to ensure correct backend format
    baseNode.type = "flow";
    baseNode.sub_type = "switch_case";

    // Use storedParameters if available, otherwise build from node.data
    if (node.data.storedParameters) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { node_type: _node_type, ...cleanParams } =
        node.data.storedParameters;
      baseNode.parameters = {
        ...cleanParams,
      };
    } else {
      baseNode.parameters = {
        mode: node.data.mode || "rules",
        rules: node.data.rules || { values: [] },
        looseTypeValidation: node.data.looseTypeValidation || true,
        options: node.data.options || {},
      };
    }
  } else if (node.type === "editFields") {
    // Explicitly set type and sub_type to ensure correct backend format
    baseNode.type = "data_transformation";
    baseNode.sub_type = "set_node";

    // Use storedParameters if available, otherwise build from individual fields
    if (node.data.storedParameters) {
      // Clean up storedParameters to avoid sending unwanted fields
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { node_type: _node_type, ...params } = node.data.storedParameters;

      // Remove selectedFields if include is not 'selected'
      if (
        params.include !== "selected" &&
        params.selectedFields !== undefined
      ) {
        delete params.selectedFields;
      }

      // Remove excludeFields if include is not 'except'
      if (params.include !== "except" && params.excludeFields !== undefined) {
        delete params.excludeFields;
      }

      baseNode.parameters = {
        ...params,
        node_type: "set", // Backend expects 'set', not 'set_node' - MUST be after spread
        ...(node.data.description && { description: node.data.description }),
      };
    } else {
      // Fallback: Transform fields array to backend format
      const transformedFields = (node.data.fields || [])
        .filter((f: any) => f.enabled && f.fieldName?.trim())
        .map((f: any) => ({
          name: f.fieldName,
          type: f.fieldValueType,
          [f.fieldValueType]: f.fieldValue,
        }));

      // Parse inputFieldsList to extract field names
      let parsedFieldsList = "";
      if (node.data.inputFieldsList && node.data.inputFieldsList.trim()) {
        const expressionMatches = node.data.inputFieldsList.match(
          /\{\{\$json\.([^}]+)\}\}/g,
        );
        if (expressionMatches) {
          const fieldNames = expressionMatches.map((match: string) =>
            match.replace(/\{\{\$json\./g, "").replace(/\}\}/g, ""),
          );
          parsedFieldsList = fieldNames.join(",");
        } else {
          parsedFieldsList = node.data.inputFieldsList;
        }
      }

      baseNode.parameters = {
        node_type: "set", // Backend expects 'set', not 'set_node'
        mode: node.data.mode || "manual",
        duplicateItem: false,
        duplicateCount: 0,
        includeOtherFields: node.data.includeOtherInputFields || false,
        include: node.data.inputFieldsMode || "all",
        fields: {
          values: transformedFields,
        },
        options: {
          ignoreConversionErrors: false,
          dotNotation: true,
          includeBinary: false,
          continueOnFail: false,
        },
        ...(node.data.inputFieldsMode === "selected" &&
          parsedFieldsList && { selectedFields: parsedFieldsList }),
        ...(node.data.inputFieldsMode === "except" &&
          parsedFieldsList && { excludeFields: parsedFieldsList }),
        title: node.data.title || "",
        ...(node.data.description && { description: node.data.description }),
      };
    }
  } else if (node.type === "savelogs") {
    // Use storedParameters if available, otherwise build from individual fields
    if (node.data.storedParameters) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { node_type: _node_type, ...cleanParams } =
        node.data.storedParameters;
      baseNode.parameters = {
        ...cleanParams,
        ...(node.data.description && { description: node.data.description }),
      };
    } else {
      // For save_to_logs nodes, transform to SaveLogNodeParameters format
      const logMappings = node.data.logMappings || [];

      // Transform logMappings to mappingFields format
      const mappingFields = logMappings
        .filter(
          (mapping: any) =>
            mapping.mappedLsField && mapping.mappedLsField.trim(),
        )
        .map((mapping: any, index: number) => ({
          fieldId: index + 1,
          rawFieldName:
            mapping.fieldPath?.replace("$json.", "") || mapping.fieldKey,
          type: "string",
          mappingLsField: mapping.mappedLsField,
          display_name: mapping.displayName || "",
          category: mapping.category || "",
          is_time: mapping.isTime || false,
        }));

      baseNode.parameters = {
        logUid: node.data.logUid || "",
        logTitile: node.data.logTitle || "", // Note: backend expects "logTitile" (typo)
        pluginId: pluginId,
        requestId: "", // Add if available from flow context
        logCategory: node.data.logCategory || "",
        log_ls_product_type: node.data.log_ls_product_type || [],
        mappingFields: mappingFields,
        options: {
          batchSize: 100,
          continueOnError: false,
          validateDataTypes: true,
          disableDotNotation: false,
        },
      };
    }
  } else if (node.type === "savedatalist") {
    // Use storedParameters if available, otherwise build from individual fields
    if (node.data.storedParameters) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { node_type: _node_type, ...cleanParams } =
        node.data.storedParameters;
      baseNode.parameters = {
        ...cleanParams,
        ...(node.data.description && { description: node.data.description }),
      };
    } else {
      // For save_to_datalist nodes, transform to SaveDatalistNodeParameters format
      const datalistMappings = node.data.datalistMappings || [];

      // Transform datalistMappings to mappingFields format
      const mappingFields = datalistMappings
        .filter(
          (mapping: any) =>
            mapping.mappedLsField &&
            mapping.mappedLsField.trim() &&
            mapping.columnType &&
            mapping.columnType.trim() &&
            mapping.dataType &&
            mapping.dataType.trim(),
        )
        .map((mapping: any, index: number) => ({
          fieldId: index + 1,
          rawFieldName:
            mapping.fieldPath?.replace("$json.", "") || mapping.fieldKey,
          type: "string",
          mappingLsField: mapping.mappedLsField,
          display_name: mapping.displayName || "",
          column_type: mapping.columnType,
          data_type: mapping.dataType,
        }));

      baseNode.parameters = {
        datalistUid: node.data.datalistUid || "",
        datalistTitle: node.data.datalistTitle || "",
        cronFrequency: node.data.cronFrequency || 0,
        cronType: node.data.cronType || "",
        groupBy: node.data.groupBy || "",
        pluginId: pluginId,
        requestId: "", // Add if available from flow context
        mappingFields: mappingFields,
        options: {
          batchSize: 100,
          continueOnError: false,
          validateDataTypes: true,
        },
      };
    }
  } else if (node.type === "splitOutNode") {
    // Handle Split Out node - explicitly set type and sub_type
    baseNode.type = "data_transformation";
    baseNode.sub_type = "split_out";

    // Map frontend enum values to backend expected values
    const mapIncludeToBackend = (frontendValue: string): string => {
      // Backend expects the same values as frontend
      switch (frontendValue) {
        case "noOtherFields":
          return "noOtherFields";
        case "allOtherFields":
          return "allOtherFields";
        case "selectedOtherFields":
          return "selectedOtherFields";
        default:
          return "noOtherFields"; // Default to noOtherFields
      }
    };

    // Use storedParameters if available, otherwise build from node.data
    if (node.data.storedParameters) {
      // Ensure fieldsToInclude is a string, not an array
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { node_type: _node_type, ...params } = node.data.storedParameters;

      // Handle fieldsToInclude - convert array to string or delete if not needed
      if (params.fieldsToInclude !== undefined) {
        // Convert array to string first if it's an array
        if (Array.isArray(params.fieldsToInclude)) {
          const filtered = params.fieldsToInclude.filter(
            (f: string) => f && f.trim(),
          );
          params.fieldsToInclude =
            filtered.length > 0 ? filtered.join(",") : "";
        }

        // Now delete if not needed (empty or wrong mode)
        if (
          !params.fieldsToInclude ||
          params.include !== "selectedOtherFields"
        ) {
          delete params.fieldsToInclude;
        }
      }

      // Ensure include value is valid - map any old incorrect values
      if (params.include === "specifiedFields") {
        // Old incorrect value - map to correct value
        params.include = "noOtherFields";
      } else if (params.include === "allFields") {
        // Old incorrect value - map to correct value
        params.include = "allOtherFields";
      } else if (
        params.include &&
        !["noOtherFields", "allOtherFields", "selectedOtherFields"].includes(
          params.include,
        )
      ) {
        // Unknown value - default to noOtherFields
        params.include = "noOtherFields";
      }

      // Clean up empty optional fields in options
      if (params.options) {
        // Keep destinationFieldName as empty string if not present (backend requires it)
        if (!params.options.destinationFieldName) {
          params.options.destinationFieldName = "";
        }
      }

      baseNode.parameters = {
        ...params,
      };
    } else {
      const splitOutFields = (node.data.fieldsToSplitOut?.[0] || "")
        .split(",")
        .map((f: string) => f.trim())
        .filter((f: string) => f.length > 0);

      const destinationField =
        node.data.options?.destinationFieldName?.trim() || "";
      const nodeOptions: any = {
        disableDotNotation: node.data.options?.disableDotNotation ?? false,
        destinationFieldName: destinationField, // Always include, even if empty string
      };

      const includeValue = node.data.include || "noOtherFields";
      baseNode.parameters = {
        fieldToSplitOut: splitOutFields.join(","),
        include: mapIncludeToBackend(includeValue),
        options: nodeOptions,
      } as any;

      // Only add fieldsToInclude when include mode is selectedOtherFields
      if (node.data.include === "selectedOtherFields") {
        if (node.data.fieldsToInclude?.[0]) {
          const includeFields = node.data.fieldsToInclude[0]
            .split(",")
            .map((f: string) => f.trim())
            .filter((f: string) => f.length > 0);
          // Only add if there are actual fields to include
          if (includeFields.length > 0) {
            (baseNode.parameters as any).fieldsToInclude =
              includeFields.join(",");
          }
        }
      }
      // For noOtherFields and allOtherFields modes, fieldsToInclude should be empty string
    }

    // Final safeguard: ensure fieldsToInclude is set correctly based on include mode
    // Backend expects this field to always exist
    if (baseNode.parameters.include !== "selectedOtherFields") {
      // Set to empty string for modes that don't use fieldsToInclude
      (baseNode.parameters as any).fieldsToInclude = "";
    } else if (baseNode.parameters.fieldsToInclude !== undefined) {
      const fieldValue = baseNode.parameters.fieldsToInclude;
      // Convert arrays to empty string, or set empty if it's an empty string
      if (
        Array.isArray(fieldValue) ||
        fieldValue === "" ||
        (typeof fieldValue === "string" && !fieldValue.trim())
      ) {
        (baseNode.parameters as any).fieldsToInclude = "";
      }
    } else {
      // Field doesn't exist, add it as empty string
      (baseNode.parameters as any).fieldsToInclude = "";
    }
  } else if (node.type === "editFieldsNode") {
    // Handle Edit Fields Node (new version) - explicitly set type and sub_type
    baseNode.type = "data_transformation";
    baseNode.sub_type = "set_node";

    // Use storedParameters if available, otherwise build from node.data
    if (node.data.storedParameters) {
      // Clean up storedParameters to avoid sending unwanted fields
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { node_type: _node_type, ...params } = node.data.storedParameters;

      // Remove selectedFields if include is not 'selected'
      if (
        params.include !== "selected" &&
        params.selectedFields !== undefined
      ) {
        delete params.selectedFields;
      }

      // Remove excludeFields if include is not 'except'
      if (params.include !== "except" && params.excludeFields !== undefined) {
        delete params.excludeFields;
      }

      baseNode.parameters = {
        ...params,
        node_type: "set", // Backend expects 'set', not 'set_node' - MUST be after spread
      };
    } else {
      const enabledFields = (node.data.fields || []).filter(
        (f: any) => f.enabled && f.fieldName?.trim(),
      );

      // Parse inputFieldsList to extract field names
      let parsedFieldsList = "";
      if (node.data.inputFieldsList && node.data.inputFieldsList.trim()) {
        const expressionMatches = node.data.inputFieldsList.match(
          /\{\{\$json\.([^}]+)\}\}/g,
        );
        if (expressionMatches) {
          const fieldNames = expressionMatches.map((match: string) =>
            match.replace(/\{\{\$json\./g, "").replace(/\}\}/g, ""),
          );
          parsedFieldsList = fieldNames.join(",");
        } else {
          parsedFieldsList = node.data.inputFieldsList;
        }
      }

      baseNode.parameters = {
        node_type: "set", // Backend expects 'set', not 'set_node'
        mode: node.data.mode || "manual",
        duplicateItem: false,
        duplicateCount: 0,
        includeOtherFields: node.data.includeOtherInputFields || false,
        include: node.data.inputFieldsMode || "all",
        fields: {
          values: enabledFields.map((f: any) => ({
            name: f.fieldName,
            type: f.fieldValueType,
            [f.fieldValueType]: f.fieldValue,
          })),
        },
        options: {
          ignoreConversionErrors: false,
          dotNotation: node.data.supportDotNotation ?? true,
          includeBinary: false,
          continueOnFail: false,
        },
        ...(node.data.inputFieldsMode === "selected" &&
          parsedFieldsList && { selectedFields: parsedFieldsList }),
        ...(node.data.inputFieldsMode === "except" &&
          parsedFieldsList && { excludeFields: parsedFieldsList }),
      };
    }
  } else if (node.type === "aggregateNode") {
    // Handle Aggregate node - explicitly set type and sub_type
    baseNode.type = "data_transformation";
    baseNode.sub_type = "aggregate";

    // Use storedParameters if available, otherwise build from node.data
    if (node.data.storedParameters) {
      // Clean up storedParameters to avoid sending unwanted fields
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { node_type: _node_type, ...params } = node.data.storedParameters;

      // Set fieldsToInclude to empty string if include is not 'specifiedFields' (backend expects field to exist)
      if (
        params.include !== "specifiedFields" ||
        !params.fieldsToInclude ||
        (typeof params.fieldsToInclude === "string" &&
          !params.fieldsToInclude.trim())
      ) {
        params.fieldsToInclude = "";
      }

      // Set fieldsToExclude to empty string if include is not 'allFieldsExcept' (backend expects field to exist)
      if (
        params.include !== "allFieldsExcept" ||
        !params.fieldsToExclude ||
        (typeof params.fieldsToExclude === "string" &&
          !params.fieldsToExclude.trim())
      ) {
        params.fieldsToExclude = "";
      }

      // Remove fieldsToAggregate if aggregate mode is not 'aggregateIndividualFields'
      if (
        params.aggregate !== "aggregateIndividualFields" &&
        params.fieldsToAggregate !== undefined
      ) {
        delete params.fieldsToAggregate;
      }

      // Ensure empty optional fields are empty strings, not removed (backend requires them)
      if (!params.destinationFieldName) {
        params.destinationFieldName = "";
      }

      baseNode.parameters = {
        ...params,
      };
    } else {
      const destFieldName = node.data.destinationFieldName?.trim() || "";

      baseNode.parameters = {
        aggregate: node.data.aggregate || "aggregateAllItemData",
        fieldsToAggregate:
          node.data.aggregate === "aggregateIndividualFields" &&
          node.data.fieldsToAggregate
            ? {
                fieldToAggregate: node.data.fieldsToAggregate.filter((f: any) =>
                  f.fieldToAggregate?.trim(),
                ),
              }
            : null,
        destinationFieldName: destFieldName, // Always include even if empty
        include: node.data.include || "allFields",
        options: {
          keepMissing: node.data.options?.keepMissing ?? false,
          mergeLists: node.data.options?.mergeLists ?? false,
          disableDotNotation: node.data.options?.disableDotNotation ?? false,
        },
      } as any;

      // Always add fieldsToInclude and fieldsToExclude (backend expects them to exist)
      // Set to actual value for correct mode, empty string otherwise
      if (node.data.include === "specifiedFields") {
        const includeFields = node.data.fieldsToInclude?.trim() || "";
        (baseNode.parameters as any).fieldsToInclude = includeFields;
      } else {
        (baseNode.parameters as any).fieldsToInclude = "";
      }

      if (node.data.include === "allFieldsExcept") {
        const excludeFields = node.data.fieldsToExclude?.trim() || "";
        (baseNode.parameters as any).fieldsToExclude = excludeFields;
      } else {
        (baseNode.parameters as any).fieldsToExclude = "";
      }
    }
  } else if (node.type === "mergeNode") {
    // Handle Merge node - explicitly set type and sub_type
    baseNode.type = "flow";
    baseNode.sub_type = "merge_items";

    // Use storedParameters if available, otherwise build from node.data
    if (node.data.storedParameters) {
      const { ...cleanParams } = node.data.storedParameters;

      baseNode.parameters = {
        ...cleanParams,
        numberOfInputs:
          node.data.numberOfInputs || cleanParams.numberOfInputs || 2,
      };
    } else {
      // Build merge node parameters including numberOfInputs
      baseNode.parameters = {
        mode: node.data.mode || "append",
        numberOfInputs: node.data.numberOfInputs || 2,
        // Append mode parameters
        ...(node.data.mode === "append" && {
          output: node.data.output || 0,
        }),
        // Combine mode parameters
        ...(node.data.mode === "combine" && {
          combineBy: node.data.combineBy || "combineByPosition",
          ...(node.data.combineBy === "combineByFields" && {
            clashHandling: node.data.clashHandling || "preferInput1",
            includeAllFields: node.data.includeAllFields ?? true,
            ...(node.data.advanced && {
              advanced: true,
              fieldsToMatchString: node.data.fieldsToMatchString || null,
              mergeByFields: node.data.mergeByFields || null,
              multipleMatches: node.data.multipleMatches || "all",
              fuzzyMatching: node.data.fuzzyMatching || false,
            }),
          }),
          disableDotNotation: node.data.disableDotNotation || false,
        }),
      };
    }
  } else if (node.type === "sortNode") {
    // Handle Sort node - explicitly set type and sub_type
    baseNode.type = "data_filtration";
    baseNode.sub_type = "sort_node";

    // Use storedParameters if available, otherwise build from node.data
    if (node.data.storedParameters) {
      // Clean up storedParameters to avoid sending unwanted fields
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { node_type: _node_type, ...params } = node.data.storedParameters;

      // Remove fields based on mode
      if (params.mode === "random") {
        // Random mode doesn't need fields or customCode
        delete params.fields;
        delete params.customCode;
        delete params.order;
      } else if (params.mode === "field") {
        // Field mode doesn't need customCode or order
        delete params.customCode;
        delete params.order;
        // Filter out empty fields
        if (params.fields && Array.isArray(params.fields)) {
          params.fields = params.fields.filter((f: any) => f.field?.trim());
        }
      } else if (params.mode === "custom") {
        // Custom mode doesn't need fields array
        delete params.fields;
      }

      baseNode.parameters = {
        ...params,
      };
    } else {
      // Build sort node parameters based on mode
      // Map UI mode back to backend mode
      const uiMode = node.data.mode || "simple";
      const backendMode =
        uiMode === "simple" ? "field" : uiMode === "code" ? "custom" : "random";

      const sortParameters: any = {
        mode: backendMode,
        nullsLast: node.data.nullsLast ?? true,
        options: {
          disableDotNotation: node.data.disableDotNotation ?? true,
        },
      };

      if (uiMode === "simple") {
        sortParameters.fields = (node.data.fields || [])
          .filter((f: any) => f.field?.trim())
          .map((f: any) => ({
            field: f.field,
            order: f.order || "asc",
          }));
      } else if (uiMode === "code") {
        sortParameters.order = node.data.order || "asc";
        sortParameters.customCode = node.data.customCode || "";
      }

      baseNode.parameters = sortParameters;
    }
  } else if (node.type === "limitNode") {
    // Handle Limit node - explicitly set type and sub_type
    baseNode.type = "data_filtration";
    baseNode.sub_type = "limit_node";

    // Use storedParameters if available, otherwise build from node.data
    if (node.data.storedParameters) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { node_type: _node_type, ...params } = node.data.storedParameters;
      baseNode.parameters = {
        ...params,
      };
    } else {
      // Build limit node parameters
      baseNode.parameters = {
        maxItems: node.data.maxItems || 10,
        keepItemsFrom: node.data.keepItemsFrom || "start",
      };
    }
  } else if (node.type === "startNode") {
    baseNode.parameters = {};
  } else if (node.type === "loopOverItemsNode") {
    // Loop Over Items node - extract batchSize and options
    if (node.data.storedParameters) {
      baseNode.parameters = {
        mode: node.data.storedParameters.mode || "splitInBatches",
        batchSize: node.data.storedParameters.batchSize || 1,
        options: {
          reset: node.data.storedParameters.options?.reset ?? false,
          keepOnlySet: node.data.storedParameters.options?.keepOnlySet ?? false,
        },
      };
    } else {
      baseNode.parameters = {
        mode: "splitInBatches",
        batchSize: node.data.batchSize || 1,
        options: {
          reset: node.data.reset ?? false,
          keepOnlySet: false,
        },
      };
    }
  } else if (node.type === "exportedMeshflow") {
    // Handle Exported Meshflow node - explicitly set type and sub_type
    baseNode.type = "core";
    baseNode.sub_type = "exportedMeshflow";

    // For exported meshflow nodes, extract only the execution output data from configData
    // Filter out metadata fields (uid, title, description, type, workflow_type, version, node_count, edge_count)
    const excludeFields = [
      "uid",
      "title",
      "description",
      "type",
      "workflow_type",
      "version",
      "node_count",
      "edge_count",
      "type_workflow",
    ];

    const configData = node.data.configData || {};
    const executionOutputData: any = {};

    // Extract only the actual execution result fields (not metadata)
    Object.entries(configData)
      .filter(([key]) => !excludeFields.includes(key))
      .forEach(([key, value]) => {
        executionOutputData[key] = value;
      });

    baseNode.parameters = executionOutputData;
  } else if (node.type === "connectFlow") {
    // Handle Connect Flow node - explicitly set type and sub_type
    baseNode.type = "core";
    baseNode.sub_type = "sub_workflow_node";

    // For connect flow nodes, use storedParameters if available
    if (node.data.storedParameters) {
      baseNode.parameters = {
        mode: node.data.storedParameters.mode || "once",
        workflow_id: node.data.storedParameters.workflow_id || "",
      };
    } else {
      baseNode.parameters = {
        mode: "once",
        workflow_id: "",
      };
    }
  } else {
    if (node.data.storedParameters) {
      const { ...cleanParams } = node.data.storedParameters;
      baseNode.parameters = {
        ...cleanParams,
        plugin_id: pluginId,
      };
    } else {
      const { ...safeData } = node.data;

      baseNode.parameters = {
        ...safeData,
        plugin_id: pluginId,
      };
    }
  }

  return baseNode;
};
